import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { platformAPI, subscriptionTiersAPI } from '../services/api'
import { 
  ArrowLeft,
  Building2, 
  Mail, 
  Palette, 
  AlertCircle,
  CheckCircle,
  Loader,
  Save,
  Upload,
  Crown,
  History,
  ArrowRightLeft,
} from 'lucide-react'
import { uploadAPI } from '../services/api'

const CHANGE_TYPE_LABEL = { upgrade: 'Upgrade', downgrade: 'Downgrade', initial: 'Inicial' }
const INTERVAL_LABEL = { monthly: 'mensal', yearly: 'anual' }

export const TenantEditPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [tenant, setTenant] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    billing_email: '',
    owner_email: '',
    domain: '',
    subscription_tier: 'starter',
    is_active: true,
    branding: {
      business_name: '',
      primary_color: '#8B5CF6',
      secondary_color: '#1F2937',
      font_family: 'Inter',
      logo_url: '',
      tagline: '',
      welcome_message: '',
      support_email: '',
      support_phone: ''
    }
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const [availableTiers, setAvailableTiers] = useState([])
  const [billingInterval, setBillingInterval] = useState('monthly')
  const [subscriptionHistory, setSubscriptionHistory] = useState([])
  const [savingSubscription, setSavingSubscription] = useState(false)
  const [subscriptionNote, setSubscriptionNote] = useState('')
  const [subscriptionMessage, setSubscriptionMessage] = useState('')

  useEffect(() => {
    if (id) {
      loadTenantData(true)
      loadAvailableTiers()
      loadSubscriptionHistory()
    }
  }, [id])

  const loadAvailableTiers = async () => {
    try {
      const res = await subscriptionTiersAPI.getAll({ include_inactive: 'true' })
      setAvailableTiers(res.data?.data || [])
    } catch (err) {
      console.error('Failed to load tiers', err)
    }
  }

  const loadSubscriptionHistory = async () => {
    try {
      const res = await platformAPI.getTenantSubscriptionHistory(id)
      setSubscriptionHistory(res.data?.data || [])
    } catch (err) {
      console.error('Failed to load history', err)
    }
  }

  const loadCurrentSubscription = async () => {
    try {
      const res = await platformAPI.getTenantSubscription(id)
      const sub = res.data?.data?.subscription
      if (sub?.billing_interval) {
        setBillingInterval(sub.billing_interval)
      }
      setTenant(prev => prev ? { ...prev, _currentBillingInterval: sub?.billing_interval || null } : prev)
    } catch (err) {
      console.error('Failed to load subscription', err)
    }
  }



  const loadTenantData = async (forceRefresh = false) => {
    // Always fetch fresh data to ensure we have the latest branding information
    setLoading(true)
    setError('')

    try {
      const response = await platformAPI.getTenant(id)
      
      // Extract the actual tenant data from the nested structure
      const tenantData = response.data.data || response.data
      
      setTenant(tenantData)
      populateForm(tenantData)
      loadCurrentSubscription()
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Estúdio não encontrado.')
      } else if (err.response?.status === 401) {
        setError('Falha na autenticação. Entre novamente.')
      } else {
        setError(`Falha ao carregar o estúdio: ${err.response?.data?.error || err.message}`)
      }
    } finally {
      setLoading(false)
    }
  }

  const populateForm = (tenantData) => {
    const brandingData = tenantData.branding || {}
    
    const newFormData = {
      name: tenantData.name || '',
      slug: tenantData.slug || '',
      billing_email: tenantData.billing_email || '',
      owner_email: tenantData.owner_email || '',
      domain: tenantData.domain || '',
      subscription_tier: tenantData.subscription_tier || 'starter',
      is_active: tenantData.is_active !== undefined ? tenantData.is_active : true,
      branding: {
        business_name: brandingData.business_name || '',
        primary_color: brandingData.primary_color || '#8B5CF6',
        secondary_color: brandingData.secondary_color || '#1F2937',
        font_family: brandingData.font_family || 'Inter',
        logo_url: brandingData.logo_url || '',
        tagline: brandingData.tagline || '',
        welcome_message: brandingData.welcome_message || '',
        support_email: brandingData.support_email || '',
        support_phone: brandingData.support_phone || ''
      }
    }
    
    setFormData(newFormData)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleBrandingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value
      }
    }))
    
    // Clear validation error for branding field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }



  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50)
  }

  const handleNameChange = (name) => {
    handleInputChange('name', name)
    
    // Auto-populate business_name if it's empty or matches the current name
    if (!formData.branding.business_name || formData.branding.business_name === formData.name) {
      handleBrandingChange('business_name', name)
    }
  }

  const validateForm = () => {
    const errors = {}
    
    // Required fields
    if (!formData.name.trim()) {
      errors.name = 'Informe o nome do estúdio'
    }
    
    if (!formData.slug.trim()) {
      errors.slug = 'Informe o slug'
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'O slug só pode conter letras minúsculas, números e hífens'
    } else if (formData.slug.length < 3) {
      errors.slug = 'O slug deve ter pelo menos 3 caracteres'
    }
    
    if (!formData.billing_email.trim()) {
      errors.billing_email = 'Informe o e-mail de cobrança'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_email)) {
      errors.billing_email = 'Informe um endereço de e-mail válido'
    }

    // Validate owner email if provided
    if (formData.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      errors.owner_email = 'Informe um endereço de e-mail válido'
    }

    // Validate domain if provided
    if (formData.domain && !/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.([a-zA-Z]{2,})+$/.test(formData.domain)) {
      errors.domain = 'Informe um domínio válido (ex.: exemplo.com.br)'
    }

    // Validate support email if provided
    if (formData.branding.support_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.branding.support_email)) {
      errors.support_email = 'Informe um e-mail de suporte válido'
    }

    // Validate support phone if provided
    if (formData.branding.support_phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.branding.support_phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.support_phone = 'Informe um telefone válido'
    }

    // No validation for logo path/URL to allow relative/static paths as well

    // Validate colors
    if (!/^#[0-9A-Fa-f]{6}$/.test(formData.branding.primary_color)) {
      errors.primary_color = 'Informe uma cor hexadecimal válida (ex.: #8B5CF6)'
    }
    
    if (!/^#[0-9A-Fa-f]{6}$/.test(formData.branding.secondary_color)) {
      errors.secondary_color = 'Informe uma cor hexadecimal válida (ex.: #1F2937)'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleLogoFileSelected = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setUploadError('')
    
    // Basic client-side validation
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      setUploadError('Selecione um arquivo de imagem (PNG, JPG, SVG etc.)')
      return
    }
    // Optional: 5MB limit
    const maxSizeBytes = 5 * 1024 * 1024
    if (file.size > maxSizeBytes) {
      setUploadError('Arquivo muito grande. O tamanho máximo é 5 MB')
      return
    }

    setUploadingLogo(true)
    try {
      const url = await uploadAPI.uploadFile(file)
      handleBrandingChange('logo_url', url)
      // Clear any URL validation error once uploaded
      setValidationErrors(prev => ({ ...prev, logo_url: '' }))
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Falha ao enviar o logo')
    } finally {
      setUploadingLogo(false)
      // Reset the input value so selecting the same file again triggers change
      event.target.value = ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setSaving(true)
    setError('')

    try {
      // Handle subscription change separately via dedicated endpoint
      // Only allow admin plan changes for tiers without Stripe price IDs
      const tierChanged = tenant && formData.subscription_tier !== tenant.subscription_tier
      const intervalChanged = tenant && billingInterval !== (tenant._currentBillingInterval || null)
      const selectedTier = availableTiers.find(t => t.name === formData.subscription_tier)
      const tierHasStripePrice = selectedTier && (selectedTier.stripe_price_id_monthly || selectedTier.stripe_price_id_yearly)

      if ((tierChanged || intervalChanged) && !tierHasStripePrice) {
        try {
          await platformAPI.changeTenantSubscription(id, {
            plan: formData.subscription_tier,
            billing_interval: billingInterval,
            note: subscriptionNote || undefined,
          })
        } catch (subErr) {
          setError(subErr.response?.data?.message || 'Falha ao alterar a assinatura')
          setSaving(false)
          return
        }
      }

      // Clean the payload - remove empty strings and unnecessary fields
      const cleanPayload = {
        name: formData.name,
        slug: formData.slug,
        billing_email: formData.billing_email,
        is_active: formData.is_active
      }

      // Add optional fields only if they have values
      if (formData.domain && formData.domain.trim()) {
        cleanPayload.domain = formData.domain.trim()
      }

      // Set/create the owner when provided (fixes "owner not found" on reset link)
      if (formData.owner_email && formData.owner_email.trim()) {
        cleanPayload.owner_email = formData.owner_email.trim()
      }

      // Clean branding object - only include fields with actual values
      const branding = {}
      if (formData.branding.business_name && formData.branding.business_name.trim()) {
        branding.business_name = formData.branding.business_name.trim()
      }
      if (formData.branding.primary_color && formData.branding.primary_color !== '#8B5CF6') {
        branding.primary_color = formData.branding.primary_color
      }
      if (formData.branding.secondary_color && formData.branding.secondary_color !== '#1F2937') {
        branding.secondary_color = formData.branding.secondary_color
      }
      if (formData.branding.font_family && formData.branding.font_family !== 'Inter') {
        branding.font_family = formData.branding.font_family
      }
      if (formData.branding.logo_url && formData.branding.logo_url.trim()) {
        branding.logo_url = formData.branding.logo_url.trim()
      }
      if (formData.branding.tagline && formData.branding.tagline.trim()) {
        branding.tagline = formData.branding.tagline.trim()
      }
      if (formData.branding.welcome_message && formData.branding.welcome_message.trim()) {
        branding.welcome_message = formData.branding.welcome_message.trim()
      }
      if (formData.branding.support_email && formData.branding.support_email.trim()) {
        branding.support_email = formData.branding.support_email.trim()
      }
      if (formData.branding.support_phone && formData.branding.support_phone.trim()) {
        branding.support_phone = formData.branding.support_phone.trim()
      }

      // Only add branding if there are actual branding fields
      if (Object.keys(branding).length > 0) {
        cleanPayload.branding = branding
      }


      
      const response = await platformAPI.updateTenant(id, cleanPayload)
      
      // Success - navigate back to tenant detail page
      navigate(`/tenants/${id}`, { replace: true })
    } catch (err) {
      setError(err.response?.data?.error || 'Falha ao atualizar o estúdio. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    navigate(`/tenants/${id}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center">
          <Loader className="h-6 w-6 animate-spin mr-2" />
          <span className="text-admin-600">Carregando dados do estúdio...</span>
        </div>
      </div>
    )
  }

  if (error && !tenant) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-admin-900 mb-2">Não foi possível carregar o estúdio</h2>
          <p className="text-admin-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/tenants')}
            className="btn btn-primary"
          >
            Voltar para Estúdios
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            aria-label="Voltar"
            className="p-2 text-admin-400 hover:text-admin-600 rounded-lg transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg mr-3">
              <Building2 className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-admin-900">Editar estúdio</h1>
              <p className="text-admin-600">Atualize as configurações e a assinatura do estúdio</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={saving}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? (
              <div className="flex items-center">
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </div>
            ) : (
              <div className="flex items-center">
                <Save className="h-4 w-4 mr-2" />
                Salvar alterações
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-admin-900 mb-4 flex items-center">
            <Building2 className="h-5 w-5 mr-2" />
            Informações básicas
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Nome do estúdio *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`input ${validationErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="Studio Maria Silva"
                  maxLength={255}
                  disabled={saving}
                />
                {validationErrors.name && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Slug *
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  className={`input font-mono text-sm ${validationErrors.slug ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="studio-maria-silva"
                  maxLength={50}
                  disabled={saving}
                />
                {validationErrors.slug && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.slug}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">Usado nas URLs (apenas minúsculas, números e hífens)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  E-mail de cobrança *
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <input
                    type="email"
                    value={formData.billing_email}
                    onChange={(e) => handleInputChange('billing_email', e.target.value)}
                    className={`input pl-10 ${validationErrors.billing_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="financeiro@studiomariasilva.com.br"
                    disabled={saving}
                  />
                </div>
                {validationErrors.billing_email && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.billing_email}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">Para faturas e pagamentos</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  E-mail do proprietário
                </label>
                <div className="relative">
                  <Crown className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => handleInputChange('owner_email', e.target.value)}
                    className={`input pl-10 ${validationErrors.owner_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="maria@studiomariasilva.com.br"
                    disabled={saving}
                  />
                </div>
                {validationErrors.owner_email && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.owner_email}</p>
                )}
                {!tenant?.owner_email ? (
                  <p className="text-amber-600 text-xs mt-1 flex items-center">
                    <AlertCircle className="h-3.5 w-3.5 mr-1" />
                    Nenhum proprietário definido. Adicione um para habilitar os links de redefinição de senha.
                  </p>
                ) : (
                  <p className="text-xs text-admin-500 mt-1">Conta do proprietário deste estúdio (pode receber links de redefinição)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Domínio personalizado
                </label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => handleInputChange('domain', e.target.value)}
                  className={`input ${validationErrors.domain ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="exemplo.com.br"
                  disabled={saving}
                />
                {validationErrors.domain && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.domain}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">Domínio personalizado opcional para este estúdio</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-2">
                  Plano de assinatura *
                </label>
                <div className="space-y-2">
                  {availableTiers.map((tier) => {
                    const hasStripePrice = tier.stripe_price_id_monthly || tier.stripe_price_id_yearly
                    const isSelected = formData.subscription_tier === tier.name
                    const isLocked = hasStripePrice && !isSelected
                    return (
                      <label key={tier.name} className="relative block">
                        <input
                          type="radio"
                          value={tier.name}
                          checked={isSelected}
                          onChange={(e) => handleInputChange('subscription_tier', e.target.value)}
                          className="sr-only"
                          disabled={saving || isLocked}
                        />
                        <div className={`p-3 border rounded-lg transition-colors ${
                          isLocked
                            ? 'border-admin-200 bg-admin-50 cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'border-primary-500 bg-primary-50 cursor-pointer'
                              : 'border-admin-200 hover:border-admin-300 cursor-pointer'
                        } ${!tier.is_active ? 'opacity-50' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm text-admin-900">{tier.display_name}</div>
                            <div className="text-xs font-medium text-admin-500">
                              ${billingInterval === 'yearly' ? tier.price_yearly : tier.price_monthly}/{billingInterval === 'yearly' ? 'ano' : 'mês'}
                            </div>
                          </div>
                          {!tier.is_active && <span className="text-[0.65rem] text-admin-400">Inativo</span>}
                          {hasStripePrice && !isSelected && (
                            <span className="text-[0.65rem] text-amber-600">Gerenciado pelo Stripe — o estúdio deve alterar pelo checkout</span>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-2">
                  Periodicidade de cobrança
                </label>
                {(() => {
                  const selectedTier = availableTiers.find(t => t.name === formData.subscription_tier)
                  const intervalLocked = selectedTier && (selectedTier.stripe_price_id_monthly || selectedTier.stripe_price_id_yearly)
                  return (
                    <>
                      <div className={`flex rounded-lg border border-admin-200 overflow-hidden ${intervalLocked ? 'opacity-60 pointer-events-none' : ''}`}>
                        <button
                          type="button"
                          onClick={() => setBillingInterval('monthly')}
                          className={`flex-1 py-2 text-sm font-medium transition-colors ${
                            billingInterval === 'monthly'
                              ? 'bg-primary-600 text-white'
                              : 'bg-white text-admin-600 hover:bg-admin-50'
                          }`}
                          aria-pressed={billingInterval === 'monthly'}
                          tabIndex={0}
                          disabled={!!intervalLocked}
                        >
                          Mensal
                        </button>
                        <button
                          type="button"
                          onClick={() => setBillingInterval('yearly')}
                          className={`flex-1 py-2 text-sm font-medium transition-colors ${
                            billingInterval === 'yearly'
                              ? 'bg-primary-600 text-white'
                              : 'bg-white text-admin-600 hover:bg-admin-50'
                          }`}
                          aria-pressed={billingInterval === 'yearly'}
                          tabIndex={0}
                          disabled={!!intervalLocked}
                        >
                          Anual
                        </button>
                      </div>
                      {intervalLocked && (
                        <p className="text-[0.65rem] text-amber-600 mt-1">A periodicidade de cobrança deste plano é gerenciada pelo Stripe</p>
                      )}
                    </>
                  )
                })()}
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="rounded border-admin-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    disabled={saving}
                  />
                  <span className="ml-2 text-sm text-admin-700">Estúdio ativo</span>
                </label>
                <p className="text-xs text-admin-500 mt-1">Estúdios inativos não conseguem acessar o painel</p>
              </div>
            </div>
          </div>
        </div>



        {/* Branding & Customization */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-admin-900 mb-4 flex items-center">
            <Palette className="h-5 w-5 mr-2" />
            Identidade visual e personalização
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Nome comercial
                </label>
                <input
                  type="text"
                  value={formData.branding.business_name}
                  onChange={(e) => handleBrandingChange('business_name', e.target.value)}
                  className="input"
                  placeholder="Studio Maria Silva"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  E-mail de suporte
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <input
                    type="email"
                    value={formData.branding.support_email}
                    onChange={(e) => handleBrandingChange('support_email', e.target.value)}
                    className={`input pl-10 ${validationErrors.support_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="contato@studiomariasilva.com.br"
                    disabled={saving}
                  />
                </div>
                {validationErrors.support_email && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.support_email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Telefone de suporte
                </label>
                <input
                  type="tel"
                  value={formData.branding.support_phone}
                  onChange={(e) => handleBrandingChange('support_phone', e.target.value)}
                  className={`input ${validationErrors.support_phone ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="+55 (11) 91234-5678"
                  disabled={saving}
                />
                {validationErrors.support_phone && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.support_phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  URL do logo
                </label>
                <input
                  type="text"
                  value={formData.branding.logo_url}
                  onChange={(e) => handleBrandingChange('logo_url', e.target.value)}
                  className={`input ${validationErrors.logo_url ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="https://exemplo.com.br/logo.png ou /assets/logo.png"
                  disabled={saving}
                />
                {validationErrors.logo_url && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.logo_url}</p>
                )}
                <p className="text-xs text-admin-500 mt-1">Cole uma URL ou envie uma imagem</p>

                {/* Upload control */}
                <div className="mt-3 flex items-center gap-3">
                  <input
                    id="logo-file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoFileSelected}
                    className="hidden"
                    disabled={saving || uploadingLogo}
                  />
                  <label htmlFor="logo-file-input">
                    <span
                      className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border transition-colors cursor-pointer ${
                        saving || uploadingLogo
                          ? 'bg-admin-100 text-admin-400 border-admin-200 cursor-not-allowed'
                          : 'bg-white text-admin-700 border-admin-300 hover:bg-admin-50'
                      }`}
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" /> Enviando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" /> Enviar imagem
                        </>
                      )}
                    </span>
                  </label>
                  {formData.branding.logo_url && (
                    <div className="flex items-center gap-2">
                      <img
                        src={formData.branding.logo_url}
                        alt="Pré-visualização do logo"
                        className="h-8 w-auto rounded border border-admin-200 bg-white p-1"
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                      <span className="text-xs text-admin-500">Pré-visualização</span>
                    </div>
                  )}
                </div>
                {uploadError && (
                  <p className="text-red-600 text-xs mt-2">{uploadError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Slogan
                </label>
                <input
                  type="text"
                  value={formData.branding.tagline}
                  onChange={(e) => handleBrandingChange('tagline', e.target.value)}
                  className="input"
                  placeholder="Seu estilo, sua essência"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Mensagem de boas-vindas
                </label>
                <textarea
                  value={formData.branding.welcome_message}
                  onChange={(e) => handleBrandingChange('welcome_message', e.target.value)}
                  className="input"
                  placeholder="Boas-vindas à nossa experiência de atendimento personalizado!"
                  rows={3}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Cor primária
                </label>
                <div className="flex">
                  <input
                    type="color"
                    value={formData.branding.primary_color}
                    onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                    className="h-10 w-16 rounded-l-lg border border-admin-300"
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.branding.primary_color}
                    onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                    className={`input rounded-l-none flex-1 font-mono text-sm ${validationErrors.primary_color ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="#8B5CF6"
                    disabled={saving}
                  />
                </div>
                {validationErrors.primary_color && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.primary_color}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Cor secundária
                </label>
                <div className="flex">
                  <input
                    type="color"
                    value={formData.branding.secondary_color}
                    onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                    className="h-10 w-16 rounded-l-lg border border-admin-300"
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.branding.secondary_color}
                    onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                    className={`input rounded-l-none flex-1 font-mono text-sm ${validationErrors.secondary_color ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="#1F2937"
                    disabled={saving}
                  />
                </div>
                {validationErrors.secondary_color && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.secondary_color}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Fonte
                </label>
                <select
                  value={formData.branding.font_family}
                  onChange={(e) => handleBrandingChange('font_family', e.target.value)}
                  className="input"
                  disabled={saving}
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Lato">Lato</option>
                  <option value="Montserrat">Montserrat</option>
                </select>
              </div>
            </div>
          </div>
        </div>



        {/* Subscription History */}
        {subscriptionHistory.length > 0 && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-admin-900 mb-4 flex items-center">
              <History className="h-5 w-5 mr-2" />
              Histórico da assinatura
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-admin-200 text-left">
                    <th className="pb-2 pr-4 font-medium text-admin-500">Data</th>
                    <th className="pb-2 pr-4 font-medium text-admin-500">Tipo</th>
                    <th className="pb-2 pr-4 font-medium text-admin-500">De</th>
                    <th className="pb-2 pr-4 font-medium text-admin-500">Para</th>
                    <th className="pb-2 pr-4 font-medium text-admin-500">Por</th>
                    <th className="pb-2 font-medium text-admin-500">Observação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-100">
                  {subscriptionHistory.map((change) => (
                    <tr key={change.id} className="text-admin-700">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-xs text-admin-500">
                        {new Date(change.created_at).toLocaleDateString('pt-BR', { month: 'short', day: 'numeric', year: 'numeric' })}
                        <br />
                        <span className="text-admin-400">
                          {new Date(change.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                          change.change_type === 'upgrade' ? 'bg-green-50 text-green-700' :
                          change.change_type === 'downgrade' ? 'bg-orange-50 text-orange-700' :
                          change.change_type === 'initial' ? 'bg-blue-50 text-blue-700' :
                          'bg-admin-100 text-admin-600'
                        }`}>
                          <ArrowRightLeft className="h-3 w-3" />
                          {CHANGE_TYPE_LABEL[change.change_type] || change.change_type}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-xs">
                        {change.previous_plan || '—'}
                        {change.previous_billing_interval && (
                          <span className="text-admin-400 ml-1">({INTERVAL_LABEL[change.previous_billing_interval] || change.previous_billing_interval})</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-medium">
                        {change.new_plan}
                        {change.new_billing_interval && (
                          <span className="text-admin-400 ml-1">({INTERVAL_LABEL[change.new_billing_interval] || change.new_billing_interval})</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-xs text-admin-500">{change.changed_by}</td>
                      <td className="py-2.5 text-xs text-admin-400 max-w-[200px] truncate">{change.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Submit Area (for mobile) */}
        <div className="lg:hidden">
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-admin-200">
            <button
              type="button"
              onClick={handleBack}
              disabled={saving}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? (
                <div className="flex items-center">
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar alterações
                </div>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
} 